import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Card, DatePicker, Statistic } from "antd";
import CountUp from "react-countup";
import moneyFormat from "functions/utils/moneyFormat";
import { bg_button } from "constant/css";
import dayjs from "dayjs";
import GetSbIncomeDetail from "functions/admin/getSbIncomeDetail";

const mainColor = "#46ce7c";
const secondaryColor = "#2a8e57";
const qpayColor = "#8adecd";
const cardColor = "#78c2b7";

const SbIncomeDetailList = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [date, setDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );
  const changeDate = (date) => {
    console.log("changing", date);
    setDate(date.format("YYYY-MM-DD"));
  };
  useEffect(() => {
    fetchData(date);
  }, [date]);

  const fetchData = async (date) => {
    console.log("searching", date);
    const response = await GetSbIncomeDetail(date);
    console.log("response", response);
    if (!response.data) return setData([]);
    setTotal(response.data.total);
    const graphData = response.data.sbIncomeByMerchants
      .filter((e) => e.total > 0)
      .map((e) => {
        return {
          commission: e.commission,
          date: e.date,
          total: e.total,
          name: e.merchant.name,
          icon: e.merchant.iconPath || "/smartbillingbg.png",
          merchantFee: e.merchantFee,
          billingSubs: e.billingSubs,
        };
      });
    console.log(graphData);
    setData(graphData);
  };

  const CustomLegend = (e) => {
    const { payload } = e;
    return (
      <div className="flex justify-center gap-6 mt-4">
        {payload.map((entry, idx) => {
          console.log(entry);
          const color = cardColor;
          const icon = (
            <img
              src="/smartbillingbg.png"
              width={22}
              height={22}
              className="rounded-md shadow-md"
              style={{ boxShadow: `0 2px 8px ${qpayColor}40` }}
            />
          );

          return (
            <div
              key={idx}
              className="flex items-center gap-2 font-semibold tracking-wide"
              style={{ color }}
            >
              {icon}
              <span>{entry.value.toUpperCase()}</span>
            </div>
          );
        })}
      </div>
    );
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-md">
          <div className="font-semibold mb-2">{label}</div>
          {payload.map((item, idx) => (
            <div
              key={idx}
              className="font-medium"
              style={{ color: item.color }}
            >
              {item.name}:{" "}
              <CountUp
                end={item.value}
                separator=","
                decimals={2}
                decimal="."
              />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatter = (value) => <CountUp end={value} separator="," />;
  const moneyFormatter = (value) => (
    <CountUp end={value} separator="," decimals={2} decimal="." />
  );

  return (
    <Card
      className="w-full mt-6 rounded-2xl shadow-lg border-0"
      bodyStyle={{ padding: 0 }}
    >
      {/* Header */}
      <div className={`p-8 rounded-t-2xl ${bg_button}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-2xl font-bold text-gray-100 tracking-wide">
              <DatePicker
                onChange={changeDate}
                format={"YYYY-MM-DD"}
                value={dayjs(date)}
                placeholder="Задаргаа харах өдөр"
                className="w-[200px]"
              />
            </div>
            <div className="text-gray-200 text-мд mt-1">Өдриин задаргаа</div>
          </div>
          <div className="flex gap-8 text-gray-100">
            <Statistic
              title="Дүн"
              value={total}
              formatter={moneyFormatter}
              valueStyle={{ fontWeight: 700, color: secondaryColor }}
            />
          </div>
        </div>
      </div>

      {/* Chart Body */}
      <div
        className={`p-8 pt-0 rounded-b-2xl bg-gradient-to-b from-[#fff] via-[#d1e6e8] to-[#d1ebe5]`}
      >
        <ResponsiveContainer width="100%" height={380}>
          <BarChart
            data={data}
            margin={{ top: 30, right: 30, left: 10, bottom: 10 }}
            barCategoryGap={24}
          >
            <defs>
              <linearGradient id="qpayGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={qpayColor} stopOpacity={0.9} />
                <stop offset="100%" stopColor="#c8e3de" stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#c4f0d9"
            />
            <XAxis
              dataKey="name"
              axisLine={{ stroke: "#97d1c4" }}
              tickLine={false}
              tick={{ fill: "#438c7c", fontSize: 15, fontWeight: 600 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: cardColor, fontSize: 14, fontWeight: 600 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
            <Bar
              dataKey="commission"
              radius={[12, 12, 0, 0]}
              fill="url(#qpayGradient)"
              barSize={28}
            >
              <LabelList
                dataKey="commission"
                position="top"
                formatter={(v) => (v > 0 ? moneyFormat(parseFloat(v)) : "")}
              />
            </Bar>
            <Bar
              dataKey="merchantFee"
              radius={[12, 12, 0, 0]}
              fill="url(#qpayGradient)"
              barSize={28}
            >
              <LabelList
                dataKey="merchantFee"
                position="top"
                formatter={(v) => (v > 0 ? moneyFormat(parseFloat(v)) : "")}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default SbIncomeDetailList;
